/**
 * @license Copyright Shutterfly, Inc. 2018 - all rights reserved
 * Originator: Shawn Rapp
 * Description: Provides a class and instance for lazyLoading scripts and styles.
 */

import * as FrontendVersionUtil from "@sfly/uig-frontend-version-util";
import { HttpHandler } from "./HttpHandler";

/**
 * A simple lazy loader.
 *
 * @class LazyLoader
 */
export class LazyLoader {
    private loadCounter: number = 0;

    /**
     * @constructor
     * @param domElem {Element} The element to attach lazy loaded files to
     */
    constructor(public domElem?: Element) {
        if (!this.domElem) {
            this.domElem = document.getElementsByTagName("head")[0];
        }
    }

    /**
     * Loads a javascript file.
     *
     * @method loadScript
     * @param scriptLocation {string} URL or relative path to script.
     * @param onLoad {function} Optional call back after script has loaded.
     */
    public loadScript(scriptLocation: string, onLoad?: () => void) {
        const newElem = document.createElement("script");
        this.loadCounter++;
        newElem.setAttribute("id", `lazy-${this.loadCounter}`);
        newElem.setAttribute("type", "text/javascript");
        newElem.setAttribute("src", scriptLocation);
        if (onLoad) {
            newElem.onload = onLoad;
        }
        this.domElem.appendChild(newElem);
    }

    /**
     * Loads a style sheet.
     *
     * @method loadStyle
     * @param styleLocation {string} URL or relative path of style sheet.
     * @param onLoad {function} Optional callback that is called after style has loaded.
     */
    public loadStyle(styleLocation: string, onLoad?: () => void) {
        const newElem = document.createElement("link");
        this.loadCounter++;
        newElem.setAttribute("id", `lazy-${this.loadCounter}`);
        newElem.setAttribute("rel", "stylesheet");
        newElem.setAttribute("type", "text/css");
        newElem.setAttribute("href", styleLocation);
        if (onLoad) {
            newElem.onload = onLoad;
        }
        this.domElem.appendChild(newElem);
    }

    /**
     * Returns if the script has been loaded already.
     * Path must be an exact match.
     *
     * @method isScriptLoaded
     * @param scriptLocation {string} URL or relative path of script.
     */
    public isScriptLoaded(scriptLocation: string): boolean {
        const searchElems = document.querySelectorAll("script");
        let retVal = false;
        searchElems.forEach((elem) => {
            if (elem.getAttribute("src") === scriptLocation) {
                retVal = true;
            }
        });

        return retVal;
    }

    /**
     * Returns if the style sheet has been loaded or not.
     *
     * @method isStyleLoaded
     * @param styleLocation {string} URL or relative path to style sheet.
     */
    public isStyleLoaded(styleLocation: string): boolean {
        const searchElems = document.querySelectorAll("link");
        let retVal = false;
        searchElems.forEach((elem) => {
            if (elem.getAttribute("href") === styleLocation) {
                retVal = true;
            }
        });

        return retVal;
    }

    /**
     * Removes the script and any enclosed references from the page.
     * Changes outside of the scripts namespace will remain.
     *
     * @method unloadScript
     * @param scriptLocation {string} URL or relative path to script
     */
    public unloadScript(scriptLocation: string) {
        const searchElems = document.querySelectorAll("script[id^=\"lazy-\"]");
        searchElems.forEach((elem) => {
            if (elem.getAttribute("src") === scriptLocation) {
                elem.remove();
            }
        });
    }

    /**
     * Removes a style sheet from the page.
     * Any elements that were styled from this sheet will be unstyled.
     *
     * @method unloadStyle
     * @param styleLocation {string} URL or relative path to style sheet.
     */
    public unloadStyle(styleLocation: string) {
        const searchElems = document.querySelectorAll("link[id^=\"lazy-\"]");
        searchElems.forEach((elem) => {
            if (elem.getAttribute("href") === styleLocation) {
                elem.remove();
            }
        });
    }

    /**
     * A simple means of figuring out what environment your in based off of the URL used.
     *
     * @method getEnv
     * @returns {string} The short name of the environment.
     */
    public getEnv() {
        const currentPage: string = document.location.href;
        let matches = currentPage.match(/(dev|beta|stage|kappa|local)?\.shutterfly.com/);
        if (!matches) {
            matches = currentPage.match(/^https?:\/\/(localhost|127\.0\.0\.1)/);

            if (!matches) { // couldn't resolve environment
                return "prod";
            }

            // matched as localhost test server
            return "test";
        }
        if (!matches[1]) { // couldn't find a environment sub domain
            return "prod";
        }

        // found environment sub domain
        return matches[1];
    }

    /**
     * This fetches the path on a CDN of the current version of a Frontend Pipeline project.
     *
     * @method fetchCDNInfo
     * @param moduleName {string} The name of the project.
     */
    public fetchCDNInfo(moduleName: string) {
        const http: HttpHandler = new HttpHandler();
        const serverEnv = this.getEnv();
        if (serverEnv === "test") {
            // return symlink path
            return Promise.resolve(`/${moduleName}`);
        }

        return FrontendVersionUtil.getUrlRoot(moduleName, {
            enablePreProdService: true
        });
    }
}

export const lazyLoader = new LazyLoader();
